import type { SenderComboboxOption } from '@/components/domain/shared/SenderCombobox';

/**
 * Curated list of common school / childcare / activity communication
 * platforms, used as type-ahead options when a parent adds a sender OUTSIDE
 * onboarding (Settings → Approved Senders, the Today Tab walkthrough). Those
 * surfaces have no live Gmail scan to draw real scanned senders from (the
 * onboarding scan isn't persisted and the OAuth provider_token is gone by
 * then), so this static list is the permission-free way to help a parent add
 * their platform quickly. It mirrors the server-side KNOWN_PLATFORMS set
 * (onboarding-suggest-domains/lib/platforms.ts) — keep the two roughly in
 * sync, but drift here only affects autocomplete suggestions, never which
 * senders are accepted.
 *
 * Each value is already `@host`-prefixed (domain form); the label is the
 * human platform name the combobox shows above the host.
 */
export const COMMON_SENDER_PLATFORMS: SenderComboboxOption[] = [
  { value: '@classdojo.com', label: 'ClassDojo' },
  { value: '@seesaw.me', label: 'Seesaw' },
  { value: '@remind.com', label: 'Remind' },
  { value: '@powerschool.com', label: 'PowerSchool' },
  { value: '@brightwheel.com', label: 'Brightwheel' },
  { value: '@parentsquare.com', label: 'ParentSquare' },
  { value: '@schoology.com', label: 'Schoology' },
  { value: '@canvas.net', label: 'Canvas' },
  { value: '@edmodo.com', label: 'Edmodo' },
  { value: '@bloomz.com', label: 'Bloomz' },
  { value: '@tadpoles.com', label: 'Tadpoles' },
  { value: '@teamsnap.com', label: 'TeamSnap' },
];

/**
 * Platforms that ANY business can send through, where the sending address
 * does not identify which tenant it belongs to.
 *
 * Mirrors the edge-side `SHARED_SENDING_PLATFORMS` minus `SCHOOL_ONLY_PLATFORMS`
 * (`onboarding-suggest-domains/lib/platforms.ts`) — keep the two roughly in
 * sync. Drift here only weakens a UX guard; the gate is unaffected, and a
 * platform missing from this list behaves exactly as everything did before.
 *
 * Used by `validateSender` to refuse a whole-domain approval and to warn on an
 * address one. The asymmetry is deliberate and matches what onboarding offers:
 * a bare `@pushpress.com` admits every gym, studio and billing operation on the
 * platform and is never suggested, so it should not be typeable either. The
 * ADDRESS is suggested — with its breadth stated — because the email that
 * opened YAR-427 came from the household's own gym, and refusing it outright
 * would cost a family the sender they actually wanted.
 */
export const SHARED_BUSINESS_PLATFORMS: readonly string[] = [
  'pushpress.com',
  'sportsengine.com',
  'daysmart.com',
  'jackrabbitclass.com',
  'smore.com',
  'blackbaud.com',
  'sparkpostmail.com',
  'mailchimp.com',
  'mcsv.net',
  'sendgrid.net',
  'mailgun.org',
  'constantcontact.com',
  'cmail19.com',
  'createsend.com',
];

/** `true` when `host` is one of the shared business platforms, or a subdomain. */
export function isSharedBusinessPlatform(host: string): boolean {
  const h = host.trim().toLowerCase().replace(/\.$/, '');
  return SHARED_BUSINESS_PLATFORMS.some((p) => h === p || h.endsWith(`.${p}`));
}
